// src/components/profile/DocumentsTable.jsx

import React, { useMemo } from 'react';

import {
  XIcon,
  CheckIcon,
} from '../common/IconsComponent';

// ─── Icons ──────────────────────────────────────────────────────────────────

const ClockIcon = ({
  size = 20,
  className = '',
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3"
    />

    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const EditIcon = ({
  size = 20,
  className = '',
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

// ─── Status ─────────────────────────────────────────────────────────────────

const STATUS_MAP = {
  APPROVED: {
    title: 'מאושר',
    pill:
      'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: (size) => (
      <CheckIcon size={size} />
    ),
  },

  REJECTED: {
    title: 'נדחה',
    pill:
      'bg-red-50 text-red-700 border-red-200',
    icon: (size) => (
      <XIcon size={size} />
    ),
  },

  NEEDS_REVISION: {
    title: 'דורש תיקון',
    pill:
      'bg-purple-50 text-purple-700 border-purple-200',
    icon: (size) => (
      <EditIcon size={size} />
    ),
  },
};

const STATUS_DEFAULT = {
  title: 'ממתין',

  pill:
    'bg-amber-50 text-amber-700 border-amber-200',

  icon: (size) => (
    <ClockIcon size={size} />
  ),
};

const getStatusView = (status) =>
  STATUS_MAP[status] ??
  STATUS_DEFAULT;

// ─── Component ──────────────────────────────────────────────────────────────

const DocumentsTable = ({
  documents,
  onDelete,
  onViewStatus,
  onViewFile,
}) => {

  const rows = useMemo(
    () =>
      Array.isArray(documents)
        ? documents
        : [],
    [documents]
  );

  // ─── Empty ───────────────────────────────────────────────────────────────

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">

        <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center mb-3">

          <svg
            className="w-6 h-6 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>

        </div>

        <p className="text-sm text-gray-500">
          אין מסמכים להצגה
        </p>

      </div>
    );
  }

  // ─── Table ───────────────────────────────────────────────────────────────

  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0">

      <table className="w-full border-collapse text-sm min-w-[420px]">

        <thead>
          <tr className="border-b border-gray-200 text-right bg-gray-50">

            <th className="px-3 sm:px-4 py-2.5 font-medium text-gray-600 w-8">
              #
            </th>

            <th className="px-3 sm:px-4 py-2.5 font-medium text-gray-600">
              מסמך
            </th>

            <th className="px-3 sm:px-4 py-2.5 font-medium text-gray-600 text-center">
              סטטוס
            </th>

            <th className="px-3 sm:px-4 py-2.5 font-medium text-gray-600 text-center">
              הערות
            </th>

            <th className="px-3 sm:px-4 py-2.5 font-medium text-gray-600 text-center">
              מחיקה
            </th>

          </tr>
        </thead>

        <tbody>

          {rows.map(
            (doc, index) => {

              const view =
                getStatusView(
                  doc.status
                );

              return (
                <tr
                  key={doc.id}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >

                  {/* Index */}
                  <td className="px-3 sm:px-4 py-3 text-gray-400 text-center">
                    {index + 1}
                  </td>

                  {/* Document */}
                  <td className="px-3 sm:px-4 py-3">

                    <button
                      type="button"
                      onClick={() =>
                        onViewFile(doc)
                      }
                      className="text-indigo-600 hover:text-indigo-800 hover:underline text-right font-medium transition-colors"
                    >
                      {doc.type}
                    </button>

                  </td>

                  {/* Status */}
                  <td className="px-3 sm:px-4 py-3">

                    <div className="flex justify-center">

                      <div
                        title={view.title}
                        className={`w-8 h-8 rounded-xl border flex items-center justify-center ${view.pill}`}
                      >
                        {view.icon(15)}
                      </div>

                    </div>

                  </td>

                  {/* Notes */}
                  <td className="px-3 sm:px-4 py-3 text-center">

                    <button
                      type="button"
                      onClick={() =>
                        onViewStatus(doc)
                      }
                      className="inline-flex items-center gap-1 bg-amber-400 hover:bg-amber-500 text-white text-xs font-medium px-2.5 py-1 rounded-full transition-colors"
                    >
                      הצג
                    </button>

                  </td>

                  {/* Delete */}
                  <td className="px-3 sm:px-4 py-3 text-center">

                    <button
                      type="button"
                      onClick={() =>
                        onDelete(doc.id)
                      }
                      title="מחיקה"
                      className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-lg hover:bg-red-50"
                    >
                      <XIcon size={16} />
                    </button>

                  </td>

                </tr>
              );
            }
          )}

        </tbody>
      </table>

    </div>
  );
};

export default DocumentsTable;